import React, { useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ForceChangePassword — modal that blocks the app until the user changes their password.
 * Shown when login returns must_change_password=true.
 */
export function ForceChangePasswordModal({ open, onSuccess }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (oldPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      // Update tokens
      if (res.data.access_token) {
        localStorage.setItem('access_token', res.data.access_token);
      }
      if (res.data.refresh_token) {
        localStorage.setItem('refresh_token', res.data.refresh_token);
      }
      toast.success('Password changed successfully');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.detail || 'Failed to change password';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <DialogTitle>Change Your Password</DialogTitle>
          </div>
          <DialogDescription>
            You must change your password before continuing. This is required for security.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="oldpw">Current Password</Label>
            <Input
              id="oldpw" type="password" value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required data-testid="change-pw-old"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="newpw">New Password</Label>
            <Input
              id="newpw" type="password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required minLength={8} data-testid="change-pw-new"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmpw">Confirm New Password</Label>
            <Input
              id="confirmpw" type="password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required data-testid="change-pw-confirm"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="change-pw-submit">
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
